<template>
  <div class="tw:h-dvh">
    <UpgradeBar />
    <Transition
      appear
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
    >
      <SplashscreenPage v-if="displaySplashscreen">
        <div
          v-if="globalDataError"
          class="q-ma-md"
        >
          There was an error loading your account.
          <button
            class="hover-active tw:border-0 tw:bg-transparent tw:p-0 tw:text-inherit cursor-pointer"
            type="button"
            @click="getAppData"
          >
            Click to retry
          </button>
        </div>
      </SplashscreenPage>
    </Transition>

    <template v-if="displayApp">
      <RouterView
        :key="routerViewKey"
        v-slot="{ Component }"
        data-testid="router-view"
      >
        <Transition
          appear
          enter-active-class="animated fadeIn"
          leave-active-class="animated fadeOut"
        >
          <Component :is="Component" />
        </Transition>
      </RouterView>

      <VueDraggableResizable
        v-show="playerOpen"
        class-name="player-popout-dragabble"
        drag-cancel=".drag-cancel"
        h="auto"
        :resizable="false"
        :style="{ width: playerPopoutWidth }"
        w="auto"
        :z="600"
      >
        <PortalTarget :name="POPOUT_PLAYER_TARGET" />
      </VueDraggableResizable>
      <Transition name="slide">
        <SideBar
          v-show="sideBarOpen"
          data-testid="side-bar"
          @close="hideSideBar"
        >
          <template v-if="sideBarSlotComponent">
            <OutreachContactSidebar
              v-if="sideBarSlotComponent === 'OutreachContactSidebar'"
            />

            <OutreachDatabaseMatchesPanel
              v-if="sideBarSlotComponent === 'OutreachDatabaseMatchesPanel'"
              :loading="sideBarData?.loading"
              :matches="databaseMatches"
              :pagination="sideBarData?.pagination"
              @pagination-changed="sideBarData?.onPaginationChange?.($event)"
            />
          </template>
        </SideBar>
      </Transition>

      <PlayerPopout
        data-testid="player-popout"
        @close="playerHide"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { PortalTarget } from "portal-vue";
import { computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import PlayerPopout from "shared/components/players/PlayerPopout.vue";
import type { ContactType } from "shared/composables/useContact";
import { useDocumentTitle } from "shared/composables/useDocumentTitle";
import { POPOUT_PLAYER_TARGET } from "shared/constants";
import { safeReplace } from "shared/helpers/routing";
import SideBar from "shared/modules/SideBar/components/SideBar/SideBar.vue";
import { useSideBarStore } from "shared/modules/SideBar/stores/useSideBarStore";
import AlertsPoller from "shared/services/polling/AlertsPoller";
import MediaRequestsPoller from "shared/services/polling/MediaRequestsPoller";
import MentionCountsPoller from "shared/services/polling/MentionCountsPoller";
import TranscriptsPoller from "shared/services/polling/TranscriptsPoller";
import { exchangeAuthHandoff } from "shared/services/tokenService";
import { useGlobalStore } from "shared/stores/global";
import { useUniversalPlayerStore } from "shared/stores/universalPlayer";
import { useUserStore } from "shared/stores/user";

import OutreachContactSidebar from "src/components/Outreach/OutreachContactSidebar";
import OutreachDatabaseMatchesPanel from "src/components/Outreach/OutreachDatabaseMatchesPanel.vue";
import SplashscreenPage from "src/components/SplashscreenPage.vue";
import UpgradeBar from "src/components/UpgradeBar";
import { GoogleLoginPoller, refreshGoogleLogin } from "src/services/googleSso";
import {
  MicrosoftLoginPoller,
  refreshMicrosoftLogin,
} from "src/services/microsoftSso";
import { useDesktopStore } from "src/stores/desktop";

defineOptions({ name: "App" });

const route = useRoute();
const router = useRouter();
const safeRouterReplace = safeReplace({ router });

useDocumentTitle();

const universalPlayerStore = useUniversalPlayerStore();
const { playerOpen, playerIsExpanded } = storeToRefs(universalPlayerStore);
const { playerHide } = universalPlayerStore;

const desktopStore = useDesktopStore();
const { getOrganisationReportSpec } = desktopStore;

const sideBarStore = useSideBarStore();

const { hideSideBar } = sideBarStore;

const { sideBarData, sideBarOpen, sideBarSlotComponent } =
  storeToRefs(sideBarStore);

const databaseMatches = computed<ContactType[]>(
  () => (sideBarData.value?.matches || []) as ContactType[]
);

const globalStore = useGlobalStore();

const { globalDataLoaded, globalDataError, routerViewKey } =
  storeToRefs(globalStore);

const { getAppData } = globalStore;

const userStore = useUserStore();

const { isLoggedIn, isAdminUser, enforceTwoFactor, identityProvider } =
  storeToRefs(userStore);

const { adminUserLogin } = userStore;

const displaySplashscreen = computed<boolean>(
  () =>
    isLoggedIn.value &&
    !globalDataLoaded.value &&
    !route.meta?.public &&
    !enforceTwoFactor.value
);

const displayApp = computed<boolean>(
  () =>
    enforceTwoFactor.value ||
    Boolean(route.meta?.public) ||
    !isLoggedIn.value ||
    (globalDataLoaded.value && !globalDataError.value)
);

const playerPopoutWidth = computed<string>(() =>
  playerIsExpanded.value ? "700px" : "347px"
);

const togglePolling = (startPolling: boolean): void => {
  if (startPolling) {
    MentionCountsPoller.init();
    AlertsPoller.init();
    MediaRequestsPoller.init();
    TranscriptsPoller.init();
  } else {
    MentionCountsPoller.stop();
    AlertsPoller.stop();
    MediaRequestsPoller.stop();
    TranscriptsPoller.stop();
    MicrosoftLoginPoller.stop();
    GoogleLoginPoller.stop();
  }
};

watch(isLoggedIn, () => togglePolling(isLoggedIn.value), { immediate: true });

async function tryAdminLogin(): Promise<void> {
  try {
    const queryParams = new URLSearchParams(window.location.search);

    const loginCode = queryParams.get("login_code");

    if (loginCode) {
      const redirect = queryParams.get("redirect") || "/";
      window.history.replaceState({}, document.title, window.location.pathname);

      const {
        access_token: token,
        account_manager: accountManager,
        refresh_token: refreshToken,
      } = await exchangeAuthHandoff(loginCode);

      await adminUserLogin({ token, refreshToken, accountManager });
      safeRouterReplace(redirect);
    }
  } catch {
    // ignore try admin login errors
  }
}

onMounted(async () => {
  // Don't do any login/data loading if public route
  if (route.meta?.public) return;

  // handle login via admin mode
  await tryAdminLogin();

  // If user is logged in, we load data needed by most of the pages
  // Otherwise we will load data after the login
  // Until global data are loaded, a spinner is displayed on
  // layouts/default instead of the router view

  if (isLoggedIn.value) {
    if (identityProvider.value === "microsoft") {
      try {
        await refreshMicrosoftLogin();
        MicrosoftLoginPoller.init();
      } catch {
        return;
      }
    }

    if (identityProvider.value === "google") {
      try {
        await refreshGoogleLogin();
        GoogleLoginPoller.init();
      } catch {
        return;
      }
    }

    await getAppData();
  }

  // Load data for the admin mode toolbar
  if (isAdminUser.value) {
    await getOrganisationReportSpec();
  }
});
</script>

<style lang="scss" scoped>
@import "shared/css/utils/transitions/slideRight";

.layout {
  width: 100%;
  transition: 0.3s;
}

.layout-wrapper {
  position: relative;
}

.player-popout-dragabble {
  border: none;
  position: fixed;
  right: 30px;
  bottom: 30px;
  z-index: 1;
}
</style>
